import q from "../styles/components/examQuestion.module.scss";
import { getQuestionTextDirection, hasMeaningfulBody, isHtmlContent } from "../assets/utils/utils";
import { resolveHtmlEmbeddedMediaUrls } from "../assets/utils/mediaUrl";
import ZoomableImage from "./ZoomableImage";
import QuestionAudioPlayer from "./QuestionAudioPlayer";

const normalizeMultilineText = (value) => {
    if (value == null) return "";
    return String(value).replace(/\\n/g, "\n");
};

const stopOptionSelect = (e) => {
    e.stopPropagation();
};

const ClearConfirmBox = ({ onConfirm, onCancel, isClearing }) => (
    <div
        className={q.clearConfirm}
        onClick={stopOptionSelect}
        role="dialog"
        aria-label="تأیید حذف پاسخ"
    >
        <p className={q.clearConfirmText}>پاسخ این گزینه حذف شود؟</p>
        <div className={q.clearConfirmActions}>
            <button
                type="button"
                className={q.clearConfirmYes}
                disabled={isClearing}
                onClick={onConfirm}
            >
                {isClearing ? "..." : "بله، حذف شود"}
            </button>
            <button
                type="button"
                className={q.clearConfirmNo}
                disabled={isClearing}
                onClick={onCancel}
            >
                انصراف
            </button>
        </div>
    </div>
);

const TestOptions = ({
    id,
    optionNo,
    optionBody,
    textDirection: parentDirection,
    layout = "block",
    imageURL,
    audioURL,
    selected = false,
    interactive = false,
    onSelect,
    showClearConfirm = false,
    onConfirmClear,
    onCancelClear,
    isClearing = false,
}) => {
    const textDirection = parentDirection || getQuestionTextDirection(optionBody);
    const isLtr = textDirection === "ltr";
    const textAlignStyle = {
        direction: textDirection,
        textAlign: isLtr ? "left" : "right",
    };
    const showBody = hasMeaningfulBody(optionBody);
    const bodyIsHtml = isHtmlContent(optionBody);
    const resolvedOptionBodyHtml = bodyIsHtml ? resolveHtmlEmbeddedMediaUrls(optionBody) : optionBody;
    const isInline = layout === "inline";

    const textContent = showBody ? (
        bodyIsHtml ? (
            <div
                className={`${q.optionText} ${q.bodyHtml}`}
                style={textAlignStyle}
                dangerouslySetInnerHTML={{ __html: resolvedOptionBodyHtml }}
            />
        ) : (
            <p className={q.optionText} style={textAlignStyle}>
                {normalizeMultilineText(optionBody)}
            </p>
        )
    ) : null;

    const optionClass = isInline ? q.optionInline : q.option;
    const directionClass = isLtr ? q.option_ltr : q.option_rtl;
    const stateClass = [
        interactive ? q.optionInteractive : "",
        selected ? q.optionSelected : "",
    ].filter(Boolean).join(" ");

    const handleSelect = interactive && onSelect
        ? () => onSelect()
        : undefined;

    const handleKeyDown = interactive && onSelect
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect();
            }
        }
        : undefined;

    if (isInline) {
        return (
            <li
                key={id}
                className={`${optionClass} ${directionClass} ${stateClass}`.trim()}
                onClick={handleSelect}
                onKeyDown={handleKeyDown}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                aria-pressed={interactive ? selected : undefined}
            >
                <span className={q.optionKey}>{optionNo}</span>
                <div className={q.optionBodyWrap}>
                    {textContent}
                    {showClearConfirm && (
                        <ClearConfirmBox
                            onConfirm={onConfirmClear}
                            onCancel={onCancelClear}
                            isClearing={isClearing}
                        />
                    )}
                </div>
            </li>
        );
    }

    return (
        <li
            key={id}
            className={`${optionClass} ${directionClass} ${stateClass}`.trim()}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-pressed={interactive ? selected : undefined}
        >
            <span className={q.optionKey}>{optionNo}</span>
            <div className={q.optionBodyWrap}>
                {textContent}
                {imageURL && (
                    <div onClick={stopOptionSelect}>
                        <ZoomableImage src={imageURL} alt="تصویر گزینه" wrapClassName={q.optionImageFrame} />
                    </div>
                )}
                {audioURL && (
                    <div onClick={stopOptionSelect}>
                        <QuestionAudioPlayer audioURL={audioURL} id={`opt-${id}`} />
                    </div>
                )}
                {showClearConfirm && (
                    <ClearConfirmBox
                        onConfirm={onConfirmClear}
                        onCancel={onCancelClear}
                        isClearing={isClearing}
                    />
                )}
            </div>
        </li>
    );
};

export default TestOptions;
